import React, { FunctionComponent, useContext } from "react";
import { Button } from "@cmsgov/ds-medicare-gov";
import { EnrollmentFormProps2021 } from "./../EnrollmentFormPage";
import { FocusableHeader } from "../../../components";
import {
  CONSTANTS as C,
  useTranslate,
  sendTealiumEvent,
} from "../../../helpers";
import { FormattedHTMLMessage } from "react-intl-phraseapp";
import { AppContext } from "../../../app/store";
import { printPage } from "../../../components/PrintButton";
import { addProtocolToUrl } from "../../../helpers/objectUtilities";
import routes from "../../../app/routes";
import PlanInfo from "../PlanInfo";

const Enrolled: FunctionComponent<EnrollmentFormProps2021> = ({
  enrollData,
  plan,
}) => {
  const t = useTranslate();
  const {
    state: { viewMedigap, language },
  } = useContext(AppContext);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enroll_form.enrolled.all_set")}
      />

      <PlanInfo plan={plan} showRequiredFieldNote={false} />

      <div className="ds-u-measure--wide">
        {plan && (
          <>
            <section className="ds-u-margin-top--4">
              <p>
                <span className="ds-u-font-weight--semibold">
                  {t("enroll_form.enrolled.name_of_person_joining_plan")}
                </span>{" "}
                <span className="ds-u-text-transform--capitalize">
                  {`${enrollData.firstName} ${enrollData.lastName}`.toLowerCase()}
                </span>
              </p>
              <span className="ds-u-font-weight--semibold">
                {t("enroll_form.enrolled.confirmation_number_for_application")}
              </span>{" "}
              <span>{enrollData.confirmationNumber}</span>
              <div>
                {t("enroll_form.enrolled.confirmation_number_disclaimer")}
              </div>
              <p>{t("enroll_form.enrolled.plan_will_review_and_process")}</p>
              <Button
                className="ds-c-button--primary"
                size="big"
                onClick={(): void => {
                  /* istanbul ignore next: tested elsewhere */
                  printPage();
                }}
              >
                {t("app.terms.print_your_application")}
              </Button>
            </section>

            <div className="EnrolledContact">
              <h2 className="ds-h3">
                {t("enroll_form.enrolled.contact_plan_with_questions")}
              </h2>
              <span>{plan.name}</span>
              <address className="ds-u-font-style--normal">
                {plan.address_1}
                <br />
                {plan.address_2 && (
                  <>
                    {plan.address_2}
                    <br />
                  </>
                )}
                {plan.city}, {plan.state} {plan.zipcode}
              </address>

              {plan.member_phone && (
                <span>
                  {t("phone")}:{` `}
                  <a href={`tel:${plan.member_phone.number}`}>
                    {plan.member_phone.number}
                  </a>
                </span>
              )}
              <span>
                {t("website")}:{` `}
                <a
                  href={addProtocolToUrl(plan.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {addProtocolToUrl(plan.url)}
                </a>
              </span>
            </div>

            <section>
              <h2 className="ds-h3">
                {t("enroll_form.enrolled.what_to_expect_now")}
              </h2>
              <ul className="Enrolled__list">
                <li>
                  <span className="ds-u-font-weight--semibold">
                    {t("enroll_form.enrolled.takes_ten_days")}
                  </span>{" "}
                  {t("enroll_form.enrolled.after_ten_days")}
                </li>
                <li>
                  <span className="ds-u-font-weight--semibold">
                    {t("enroll_form.enrolled.plan_will_notify_approval")}
                  </span>{" "}
                  {t("enroll_form.enrolled.plan_will_give_info")}
                </li>
                <li>
                  <div className="ds-u-font-weight--semibold ds-u-margin-bottom--2">
                    {t("enroll_form.enrolled.fill_prescription_before_card")}
                  </div>
                  <ul className="Enrolled_list">
                    <FormattedHTMLMessage id="enroll_form.enrolled.fill_prescription_before_card_steps" />
                  </ul>
                </li>
              </ul>
              <p className="ds-u-margin-top--5">
                {t("enroll_form.enrolled.call_plan_with_questions")}
              </p>
            </section>

            {viewMedigap && (
              <div className="ds-u-padding--3 ds-u-margin-y--3 ds-u-fill--gray-lightest">
                <h2 className="ds-h3">
                  {t("enroll_form.also_chosen_medigap_long")}
                </h2>
                <a
                  className="ds-c-button ds-c-button--primary-alt"
                  href={`#${routes.medigap.landingPage}?lang=${language}`}
                  onClick={(): void => {
                    // istanbul ignore next: tested elsewhere
                    sendTealiumEvent("Plan Enroll", "Going to Medigap");
                  }}
                  data-testid="medigapLink"
                >
                  {t("landing_page.coverage_selection.view_policies")}
                </a>
              </div>
            )}
          </>
        )}
        <a
          href={C.linkToMedicareHome(language)}
          className="ds-c-button ds-c-button--primary ds-c-button--big"
        >
          {t("done")}
        </a>
      </div>
    </div>
  );
};

export default Enrolled;
